import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TabData } from '@/__core/types/TabData';
import { useTabStorage } from '@/__core/hooks/useTabStorage';
import { normalizePermissionCompanyId } from '@/__core/permissions/utils/moduleCompanyPermissions';
import {
  filterTabsByToolbarCompany,
  isTabVisibleForToolbarCompany,
} from '@/__core/permissions/utils/moduleTabVisibility';

export const PENDING_TAB_CLOSE = -1;
export const PENDING_TAB_ADD = -2;
const PENDING_TAB_CUSTOM = -3;

const ADD_TAB_ID = 10;
const LIST_TAB_ID = 0;

export interface UseModuleTabsWithGuardOptions {
  storageKey: string;
  defaultTabs: TabData[];
  maxTabs?: number;
  canCreate: boolean;
  /** Tab types checked for unsaved changes (e.g. new-user, update-user) */
  guardedTabTypes: string[];
  buildNewTab: (tabs: TabData[]) => Omit<TabData, 'createdAt'>;
  /** Called after switching tabs (clear success/error messages, etc.) */
  onAfterTabChange?: () => void;
  allowMultipleTabTypes?: string[];
  /**
   * Toolbar company filter (`activeCompanyFilter`). When set, non-matching edit/new tabs
   * are hidden from `visibleTabs` / header while kept in storage.
   */
  toolbarCompanyId?: string | null;
}

export function useModuleTabsWithGuard({
  storageKey,
  defaultTabs,
  maxTabs = 20,
  canCreate,
  guardedTabTypes,
  buildNewTab,
  onAfterTabChange,
  allowMultipleTabTypes,
  toolbarCompanyId,
}: UseModuleTabsWithGuardOptions) {
  /** Stamp toolbar company on the "+" tab so it hides when the filter changes. */
  const enrichedDefaultTabs = useMemo(() => {
    const filter = normalizePermissionCompanyId(toolbarCompanyId);
    if (!filter || filter === 'all' || filter === 'global') {
      return defaultTabs;
    }

    return defaultTabs.map((tab) => {
      if (tab.type !== 'add') {
        return tab;
      }

      return {
        ...tab,
        metadata: {
          ...tab.metadata,
          companyId: filter,
        },
      };
    });
  }, [defaultTabs, toolbarCompanyId]);

  const {
    tabs: storedTabs,
    activeTab,
    setActiveTab,
    addTab,
    removeTab,
    updateTab,
    clearTabs,
  } = useTabStorage({
    storageKey,
    defaultTabs: enrichedDefaultTabs,
    maxTabs,
    allowMultipleTabTypes,
  });

  const [pendingActiveTabId, setPendingActiveTabId] = useState<number | null>(null);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);

  const hasUnsavedChangesRef = useRef(false);
  const tabUnsavedChangesRef = useRef(new Map<number, boolean>());
  const pendingActionRef = useRef<(() => void) | null>(null);

  const guardedTypeSet = useMemo(() => new Set(guardedTabTypes), [guardedTabTypes]);

  const visibleTabs = useMemo(() => {
    if (toolbarCompanyId === undefined) {
      return storedTabs;
    }
    return filterTabsByToolbarCompany(storedTabs, toolbarCompanyId);
  }, [storedTabs, toolbarCompanyId]);

  const visibleActiveTab = useMemo(() => {
    const currentTab = storedTabs[activeTab];
    if (!currentTab) return 0;
    const index = visibleTabs.findIndex((tab) => tab.id === currentTab.id);
    return index >= 0 ? index : 0;
  }, [activeTab, storedTabs, visibleTabs]);

  useEffect(() => {
    if (toolbarCompanyId === undefined) return;

    const currentTab = storedTabs[activeTab];
    if (!currentTab || isTabVisibleForToolbarCompany(currentTab, toolbarCompanyId)) {
      return;
    }

    const listIndex = storedTabs.findIndex(
      (tab) => tab.type === 'list' || tab.id === LIST_TAB_ID
    );
    if (listIndex >= 0 && listIndex !== activeTab) {
      setActiveTab(listIndex);
      onAfterTabChange?.();
    }
  }, [activeTab, onAfterTabChange, setActiveTab, storedTabs, toolbarCompanyId]);

  const tabs = useMemo(
    () =>
      visibleTabs.map((tab) => ({
        ...tab,
        active: tab.id === storedTabs[activeTab]?.id,
      })),
    [visibleTabs, storedTabs, activeTab]
  );

  useEffect(() => {
    if (pendingActiveTabId === null) return;
    const newTabIndex = storedTabs.findIndex((tab) => tab.id === pendingActiveTabId);
    if (newTabIndex !== -1) {
      setActiveTab(newTabIndex);
      setPendingActiveTabId(null);
    }
  }, [pendingActiveTabId, setActiveTab, storedTabs]);

  const syncGlobalUnsavedRef = useCallback(
    (tabIndex: number) => {
      const tab = storedTabs[tabIndex];
      if (tab?.id !== undefined) {
        hasUnsavedChangesRef.current = tabUnsavedChangesRef.current.get(tab.id) || false;
      } else {
        hasUnsavedChangesRef.current = false;
      }
    },
    [storedTabs]
  );

  const isGuardedTab = useCallback((type: string) => guardedTypeSet.has(type), [guardedTypeSet]);

  const getCurrentGuardedUnsaved = useCallback(() => {
    const currentTab = storedTabs[activeTab];
    if (!currentTab || !isGuardedTab(currentTab.type)) {
      return false;
    }
    return currentTab.id !== undefined
      ? tabUnsavedChangesRef.current.get(currentTab.id) || false
      : hasUnsavedChangesRef.current;
  }, [activeTab, isGuardedTab, storedTabs]);

  const handleUnsavedChangesUpdate = useCallback(
    (hasChanges: boolean, tabId?: number) => {
      if (tabId !== undefined) {
        tabUnsavedChangesRef.current.set(tabId, hasChanges);
        const currentTab = storedTabs[activeTab];
        if (currentTab?.id === tabId) {
          hasUnsavedChangesRef.current = hasChanges;
        }
      } else {
        hasUnsavedChangesRef.current = hasChanges;
      }
    },
    [activeTab, storedTabs]
  );

  const activateTabIndex = useCallback(
    (index: number) => {
      setActiveTab(index);
      onAfterTabChange?.();
      syncGlobalUnsavedRef(index);
    },
    [onAfterTabChange, setActiveTab, syncGlobalUnsavedRef]
  );

  const createAndOpenNewTab = useCallback(() => {
    const newTab = buildNewTab(storedTabs);
    addTab(newTab);
    setPendingActiveTabId(newTab.id);
  }, [addTab, buildNewTab, storedTabs]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const visibleTab = visibleTabs[newValue];
      const storedIndex = visibleTab
        ? storedTabs.findIndex((tab) => tab.id === visibleTab.id)
        : newValue;
      const resolvedIndex = storedIndex >= 0 ? storedIndex : newValue;
      const newTab = storedTabs[resolvedIndex];

      if (newTab?.type === 'add' || newTab?.id === ADD_TAB_ID) {
        return;
      }

      const currentTab = storedTabs[activeTab];
      if (
        currentTab &&
        isGuardedTab(currentTab.type) &&
        (tabUnsavedChangesRef.current.get(currentTab.id) || false)
      ) {
        setPendingTabChange(resolvedIndex);
        setUnsavedChangesDialog(true);
        return;
      }

      activateTabIndex(resolvedIndex);
    },
    [activateTabIndex, activeTab, isGuardedTab, storedTabs, visibleTabs]
  );

  const handleVisibleTabChange = useCallback(
    (_event: React.SyntheticEvent, visibleIndex: number) => {
      handleTabChange(_event, visibleIndex);
    },
    [handleTabChange]
  );

  const handleCloseTab = useCallback(
    (tabId: number) => {
      if (tabId === LIST_TAB_ID || tabId === ADD_TAB_ID) return;

      const tabToClose = storedTabs.find((tab) => tab.id === tabId);
      const currentTab = storedTabs[activeTab];
      const isClosingActiveTab = tabId === currentTab?.id;
      const hasTabUnsavedChanges = tabUnsavedChangesRef.current.get(tabId) || false;

      if (
        tabToClose &&
        isGuardedTab(tabToClose.type) &&
        isClosingActiveTab &&
        hasTabUnsavedChanges
      ) {
        setPendingTabChange(PENDING_TAB_CLOSE);
        setUnsavedChangesDialog(true);
        return;
      }

      tabUnsavedChangesRef.current.delete(tabId);
      if (isClosingActiveTab) {
        hasUnsavedChangesRef.current = false;
      }
      removeTab(tabId);
    },
    [activeTab, isGuardedTab, removeTab, storedTabs]
  );

  const handleAddTab = useCallback(() => {
    if (!canCreate) return;

    if (getCurrentGuardedUnsaved()) {
      setPendingTabChange(PENDING_TAB_ADD);
      setUnsavedChangesDialog(true);
      return;
    }

    createAndOpenNewTab();
  }, [canCreate, createAndOpenNewTab, getCurrentGuardedUnsaved]);

  const guardAndRun = useCallback(
    (action: () => void) => {
      if (getCurrentGuardedUnsaved()) {
        pendingActionRef.current = action;
        setPendingTabChange(PENDING_TAB_CUSTOM);
        setUnsavedChangesDialog(true);
        return;
      }
      action();
    },
    [getCurrentGuardedUnsaved]
  );

  const openTab = useCallback(
    (tab: Omit<TabData, 'createdAt'>) => {
      const existingIndex = storedTabs.findIndex((stored) => {
        if (stored.type !== tab.type) return false;
        if (tab.entityId && stored.entityId) {
          return stored.entityId === tab.entityId;
        }
        return false;
      });

      if (existingIndex !== -1) {
        updateTab(storedTabs[existingIndex].id, {
          entityData: tab.entityData,
          metadata: tab.metadata,
          title: tab.title,
        });
        setActiveTab(existingIndex);
        return existingIndex;
      }

      addTab(tab);
      setPendingActiveTabId(tab.id);
      return null;
    },
    [addTab, setActiveTab, storedTabs, updateTab]
  );

  const handleConfirmLeaveWithoutSaving = useCallback(() => {
    setUnsavedChangesDialog(false);
    hasUnsavedChangesRef.current = false;

    const customAction = pendingActionRef.current;
    if (customAction) {
      pendingActionRef.current = null;
      setPendingTabChange(null);
      customAction();
      return;
    }

    const targetTab = pendingTabChange;
    setPendingTabChange(null);

    if (targetTab === PENDING_TAB_ADD) {
      if (canCreate) {
        createAndOpenNewTab();
      }
    } else if (targetTab === PENDING_TAB_CLOSE) {
      const currentTab = storedTabs[activeTab];
      if (currentTab && currentTab.id !== LIST_TAB_ID && currentTab.id !== ADD_TAB_ID) {
        tabUnsavedChangesRef.current.delete(currentTab.id);
        removeTab(currentTab.id);
      }
    } else if (targetTab !== null && targetTab >= 0) {
      activateTabIndex(targetTab);
    }
  }, [
    activateTabIndex,
    activeTab,
    canCreate,
    createAndOpenNewTab,
    pendingTabChange,
    removeTab,
    storedTabs,
  ]);

  const handleCancelLeave = useCallback(() => {
    setUnsavedChangesDialog(false);
    setPendingTabChange(null);
    pendingActionRef.current = null;
  }, []);

  const isStoredTabPanelActive = useCallback(
    (tab: TabData) => tab.id === storedTabs[activeTab]?.id,
    [storedTabs, activeTab]
  );

  return {
    storedTabs,
    visibleTabs,
    visibleActiveTab,
    activeTab,
    setActiveTab,
    addTab,
    removeTab,
    updateTab,
    clearTabs,
    tabs,
    pendingActiveTabId,
    setPendingActiveTabId,
    handleTabChange,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog: {
      open: unsavedChangesDialog,
      onClose: handleCancelLeave,
      onConfirmLeave: handleConfirmLeaveWithoutSaving,
    },
  };
}
